/**
 * Pure transform utilities that convert `GET enrichments/{batchId}/insights`
 * wire rows into the item arrays consumed by AIPerformanceInsights,
 * AttributeScoreList, and AttributeComparisonList.
 *
 * Kept at the global utils layer alongside `coverage-transforms.ts` so the
 * normalisation logic stays out of the service/mapper layer.
 */

import type { AIPerformanceInsight } from '~/components/common/ai-performance-insights';
import type { AttributeComparisonItem } from '~/components/common/attribute-comparison-list';
import type { AttributeItem } from '~/components/common/attribute-score-list';
import type {
  InsightCountRowWire,
  InsightScoreRowWire,
  InsightTermRowWire,
} from '~/features/catalog-operations/types/catalog-operations.api.types';
import type {
  BeforeAfterCount,
  PerformanceAttributeItem,
  PerformanceTermItem,
} from '~/features/catalog-operations/types/catalog-operations.types';

/**
 * Converts PRODUCT_TERM rows into AIPerformanceInsights table rows. Share
 * percentages arrive as raw numbers (e.g. `3.45`) and are formatted to two
 * decimal places with a `%` suffix.
 */
export function insightTermsToPerformanceInsights(
  rows: InsightTermRowWire[],
): AIPerformanceInsight[] {
  return rows.map((row) => ({
    term: row.label,
    monthlySearchCount: row.monthlySearches,
    impressionShare: {
      beforePercent: `${row.shareBefore.toFixed(2)}%`,
      afterPercent: `${row.shareAfter.toFixed(2)}%`,
    },
    terms: { beforeCount: row.countBefore, afterCount: row.countAfter },
    // Paired fields on the wire — null exactly together — but each is
    // checked independently rather than trusting that pairing holds forever.
    competition:
      row.competition != null && row.competitionIndex != null
        ? {
            level: row.competition.toLowerCase() as 'low' | 'medium' | 'high',
            index: row.competitionIndex,
          }
        : null,
  }));
}

/**
 * Collapses the duplicate period rows POPULAR_ATTRIBUTES emits. Every run
 * writes each attribute twice — once MONTHLY, once CUMULATIVE, with identical
 * values, because attributes aren't time-sliced. Without this the card renders
 * two bars per attribute.
 *
 * Keyed by `productCategory` + `label`, not label alone — POPULAR_ATTRIBUTES
 * sends one aggregate row per label (`productCategory: null`) *and* a
 * separate row per label per category the catalog spans, each a genuinely
 * different value (e.g. "Fit" catalog-wide vs. "Fit" scoped to
 * apparel-and-accessories), not a period duplicate to collapse together.
 * `AttributeScoreList` renders both kinds of row together for "All" and
 * filters down to one category's rows otherwise.
 *
 * A row carrying counts always beats one without, so a backfill that populates
 * only one period type can't be thrown away by the tie-break; CUMULATIVE only
 * decides between two rows that are equally informative.
 */
function dedupeByPeriod(rows: InsightScoreRowWire[]): InsightScoreRowWire[] {
  const hasCounts = (row: InsightScoreRowWire) => row.countAfter != null || row.countBefore != null;
  const byKey = new Map<string, InsightScoreRowWire>();
  for (const row of rows) {
    const key = `${row.productCategory ?? ''}::${row.label}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }
    if (hasCounts(existing) && !hasCounts(row)) continue;
    if (hasCounts(row) && !hasCounts(existing)) {
      byKey.set(key, row);
      continue;
    }
    if (row.periodType === 'CUMULATIVE') byKey.set(key, row);
  }
  return [...byKey.values()];
}

/**
 * Recovers a score from its raw count and a shared cohort size when the wire
 * sends a null score for an otherwise-present count (now common for
 * POPULAR_ATTRIBUTES — the score simply isn't computed for that attribute on
 * some runs, independently of whether the count is). Falls back to `0` only
 * when there's truly nothing to compute from — the pre-existing silent
 * behavior for rows with neither a score nor a usable count.
 */
function backfillScore(
  score: number | null,
  count: number | null | undefined,
  sampleSize: number | null | undefined,
  maxScore: number,
): number {
  if (score != null) return score;
  if (count != null && sampleSize) return (count / sampleSize) * maxScore;
  return 0;
}

/**
 * Converts score-based rows (POPULAR_ATTRIBUTES, on the component's 0–10
 * scale) into AttributeScoreList items. A row whose score came back `null`
 * has its score backfilled from `count / sampleSize × maxScore` — the same
 * relationship {@link deriveAttributeSampleSize} already trusts in reverse —
 * so a run that only computed counts for an attribute still gets a real bar
 * instead of a silent 0%-width one. Raw product counts and monthly search
 * volume ride along for the row's value readout and subtitle regardless.
 * Rows are deduped by period and ordered by `rank`, which the backend
 * guarantees to be contiguous 1..N display order.
 */
export function insightScoresToAttributeItems(
  rows: InsightScoreRowWire[],
  maxScore = 10,
): AttributeItem[] {
  const sampleSize = deriveAttributeSampleSize(rows);
  return dedupeByPeriod(rows)
    .sort((a, b) => a.rank - b.rank)
    .map((row) => ({
      title: row.label,
      beforeScore: backfillScore(row.scoreBefore, row.countBefore, sampleSize, maxScore),
      afterScore: backfillScore(row.scoreAfter, row.countAfter, sampleSize, maxScore),
      beforeCount: row.countBefore ?? null,
      afterCount: row.countAfter ?? null,
      monthlySearches: row.monthlySearches ?? null,
      productCategory: row.productCategory ?? null,
      sampleSize: row.sampleSize ?? null,
      maxScore,
    }));
}

/**
 * Derives the shared cohort size behind the attribute counts — the "of 2,000
 * products" denominator. Now sent directly on every row (`sampleSize`); that's
 * preferred when present. Falls back to deriving it for payloads that predate
 * the field — both sides of every attribute share one denominator by
 * construction, so `count × 10 / score` recovers it exactly from any row that
 * has both. Undefined when no row qualifies (all-zero cohort, or a run that
 * predates both the field and the counts).
 */
export function deriveAttributeSampleSize(rows: InsightScoreRowWire[]): number | undefined {
  const wireSampleSize = rows.find((row) => row.sampleSize != null)?.sampleSize;
  if (wireSampleSize != null) return wireSampleSize;
  // Scans the raw rows, not the deduped set — every period row shares the one
  // cohort, so any row that has both parts can answer, even a discarded twin.
  for (const row of rows) {
    if (row.scoreAfter != null && row.scoreAfter > 0 && row.countAfter != null) {
      return Math.round((row.countAfter * 10) / row.scoreAfter);
    }
  }
  return undefined;
}

/**
 * Converts score-based rows (OCCASIONS_COVERAGE, 0–100 scale) into
 * AttributeComparisonList items. `maxValue` is fixed at 100 since the values
 * are already percentages. A null score is backfilled from `count /
 * sampleSize × 100` when the wire sends a `sampleSize` — defensive: no
 * occasion row has actually shown up with a null score yet, but it's the same
 * wire shape that already broke for POPULAR_ATTRIBUTES, so it's guarded the
 * same way rather than waiting for it to also render "null%".
 */
export function insightScoresToComparison(rows: InsightScoreRowWire[]): {
  items: AttributeComparisonItem[];
  maxValue: number;
} {
  const sampleSize = rows.find((row) => row.sampleSize != null)?.sampleSize;
  return {
    items: rows.map((row) => ({
      title: row.label,
      beforeValue: backfillScore(row.scoreBefore, row.countBefore, sampleSize, 100),
      afterValue: backfillScore(row.scoreAfter, row.countAfter, sampleSize, 100),
      // Rides along for the drill-down drawer's "Products tagged" header
      // stat — only set when both sides are real counts, never a 0 standing
      // in for "not recorded".
      ...(row.countBefore != null && row.countAfter != null
        ? { countBefore: row.countBefore, countAfter: row.countAfter }
        : {}),
    })),
    maxValue: 100,
  };
}

/**
 * Merges the three trend groups (TOP_TRENDS, MICRO_TRENDS, MACRO_TRENDS) into a
 * single AttributeComparisonList dataset. Micro/macro rows are tagged with a
 * `trendKind` so the UI can badge them; top rows stay untagged. Counts are
 * open-ended, so `maxValue` is derived from the largest before/after value
 * across the merged set with 20% headroom, rather than a fixed ceiling.
 */
export function insightTrendsToComparison(groups: {
  top: InsightCountRowWire[];
  micro: InsightCountRowWire[];
  macro: InsightCountRowWire[];
}): { items: AttributeComparisonItem[]; maxValue: number } {
  const toItem = (row: InsightCountRowWire, trendKind?: 'micro' | 'macro') => ({
    title: row.label,
    beforeValue: row.countBefore,
    afterValue: row.countAfter,
    ...(trendKind ? { trendKind } : {}),
  });
  const items: AttributeComparisonItem[] = [
    ...groups.top.map((row) => toItem(row)),
    ...groups.micro.map((row) => toItem(row, 'micro')),
    ...groups.macro.map((row) => toItem(row, 'macro')),
  ];
  if (items.length === 0) return { items: [], maxValue: 1 };
  const rawMax = Math.max(...items.map((item) => Math.max(item.beforeValue, item.afterValue)), 1);
  return { items, maxValue: Math.ceil(rawMax * 1.2) };
}

/** Sums `countAfter` across insight rows that carry it (PRODUCT_TERM, TOP_TRENDS). */
function sumCountAfter(rows: { countAfter: number }[]): number {
  return rows.reduce((sum, row) => sum + row.countAfter, 0);
}

/**
 * Sums `countAfter` across TOP_TRENDS rows — total trend-tag applications
 * after enrichment. Powers the batch detail page's "Trends Applied" top
 * metric card.
 *
 * `null` when there are no rows: the signal wasn't computed for this batch,
 * which the card shows as "—". A real 0 needs rows that matched nothing.
 */
export function sumTrendsApplied(rows: InsightCountRowWire[]): number | null {
  return rows.length === 0 ? null : sumCountAfter(rows);
}

/**
 * Sums `countAfter` across PRODUCT_TERM rows — total search-term matches
 * after enrichment. Powers the batch detail page's "Search Terms Covered"
 * top metric card.
 *
 * `null` when there are no rows (no search terms were measured for this
 * batch) — shown as "—", not as a 0 nobody measured.
 */
export function sumSearchTermsCovered(rows: InsightTermRowWire[]): number | null {
  return rows.length === 0 ? null : sumCountAfter(rows);
}

/**
 * Counts NEWLY-ADDED occasions among OCCASIONS_COVERAGE rows — an occasion the
 * batch had no products for before enrichment (`countBefore` net 0) and does
 * after (`countAfter` > 0). Powers the batch detail page's "Occasion Tags
 * Applied" top metric card.
 *
 * Older runs omit the counts entirely (scores only); such rows can't be shown
 * to be newly added, so they don't count — `countAfter` must be a real positive
 * number, never a null coerced to 0.
 *
 * `null` when there are no rows (occasion coverage wasn't computed) — "—".
 */
export function sumOccasionTagsApplied(rows: InsightScoreRowWire[]): number | null {
  if (rows.length === 0) return null;
  return rows.filter((row) => (row.countBefore ?? 0) === 0 && (row.countAfter ?? 0) > 0).length;
}

/**
 * Counts distinct product terms with at least one matching product, before and
 * after enrichment. Distinct from `sumSearchTermsCovered` (which sums match
 * *counts*, not term breadth) — powers the Performance Signals stat cards'
 * "Search terms matched" card.
 */
export function countTermsMatched(rows: PerformanceTermItem[]): BeforeAfterCount {
  return {
    before: rows.filter((row) => row.terms.beforeCount > 0).length,
    after: rows.filter((row) => row.terms.afterCount > 0).length,
  };
}

/** Total monthly search volume plus the count of terms behind it, for
 *  {@link sumNewlyMatchedSearchVolume}. */
export interface NewlyMatchedSearchVolume {
  volume: number;
  termCount: number;
}

/**
 * Sums the monthly search volume of terms enrichment newly matched (zero
 * products before, at least one after) — the demand a batch's enrichment
 * opened up that wasn't reachable before. `termCount` rides along so the card
 * can say "across N terms" without a second pass over the rows. Powers the
 * Performance Signals stat cards' "New monthly search volume reached" card.
 */
export function sumNewlyMatchedSearchVolume(rows: PerformanceTermItem[]): NewlyMatchedSearchVolume {
  const newlyMatched = rows.filter(
    (row) => row.terms.beforeCount === 0 && row.terms.afterCount > 0,
  );
  return {
    termCount: newlyMatched.length,
    // A row whose `monthlySearchCount` isn't a real number (seen in practice
    // despite the wire type claiming `number` — the sibling `beforeCount`/
    // `afterCount` fields are documented as nullable on the same payload)
    // contributes 0 rather than poisoning the whole sum to NaN.
    volume: newlyMatched.reduce(
      (sum, row) => sum + (Number.isFinite(row.monthlySearchCount) ? row.monthlySearchCount : 0),
      0,
    ),
  };
}

/**
 * Averages the Popular Product Attributes' 0–10 scores into a single
 * before/after completeness percentage. An unweighted mean across attributes,
 * not a literal "% of products with every attribute populated" — the backend
 * doesn't send a single denominator across attributes to compute that exactly
 * (`insightScoresToAttributeItems` already backfills a null wire score from
 * count/sampleSize, so `beforeScore`/`afterScore` here are real numbers for
 * any row with either a score or a count). `undefined` when there are no rows
 * with usable scores to average — this filter is now a last-resort guard for
 * a row with neither a score nor a count, not the common case it used to be.
 */
export function averageAttributeCompleteness(
  rows: PerformanceAttributeItem[],
): BeforeAfterCount | undefined {
  const valid = rows.filter(
    (row) => Number.isFinite(row.beforeScore) && Number.isFinite(row.afterScore),
  );
  if (valid.length === 0) return undefined;
  const avgOf = (pick: (row: PerformanceAttributeItem) => number) =>
    valid.reduce((sum, row) => sum + pick(row), 0) / valid.length;
  return {
    before: Math.round(avgOf((row) => row.beforeScore) * 10),
    after: Math.round(avgOf((row) => row.afterScore) * 10),
  };
}
